import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Mail, Edit, Send, Check, X, DollarSign } from "lucide-react";
import { quoteStatusClass } from "@/components/reports/QuotesReport";

const money = (n) => `$${(n || 0).toFixed(2)}`;
const isExpired = (q) => q.valid_until && q.valid_until < new Date().toISOString().slice(0, 10) && q.status !== "approved";

const Row = ({ label, value, cls = "" }) => (
  <div className="flex justify-between text-sm py-1"><span className="text-slate-500">{label}</span><span className={`font-medium text-slate-800 ${cls}`}>{value}</span></div>
);

export const QuoteQuickView = ({ quote, onClose, actions, currentUser }) => {
  if (!quote) return null;
  const run = (fn) => () => { onClose(); fn(); };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="quote-quick-view">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3 flex-wrap">
            <span data-testid="quote-quick-view-number">{quote.quote_number}</span>
            <Badge className={quoteStatusClass(quote.status)} data-testid="quote-quick-view-status">{quote.status.replace("_", " ").toUpperCase()}</Badge>
            {isExpired(quote) && <Badge className="bg-red-100 text-red-700 border-red-200">EXPIRED</Badge>}
            {quote.customer_decision && <Badge className={quote.customer_decision === "accepted" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"} data-testid="quote-qv-customer-decision">CUSTOMER {quote.customer_decision.toUpperCase()}</Badge>}
            {quote.converted_invoice_number && <Badge className="bg-blue-100 text-blue-800" data-testid="quote-qv-invoiced">INVOICED {quote.converted_invoice_number}</Badge>}
          </SheetTitle>
          <SheetDescription>{quote.customer_name} · created by {quote.created_by}{quote.approved_by ? ` · approved by ${quote.approved_by}` : ""}</SheetDescription>
        </SheetHeader>
        {quote.customer_decision && (
          <div className={`mt-4 rounded-lg p-3 text-sm ${quote.customer_decision === "accepted" ? "bg-emerald-50 text-emerald-900" : "bg-slate-100 text-slate-700"}`} data-testid="quote-qv-decision-details">
            <p className="font-semibold">{quote.customer_decision === "accepted" ? "Approved" : "Declined"} by {quote.customer_decision_name} on {String(quote.customer_decision_at || "").slice(0, 10)}</p>
            {quote.customer_decision_note && <p className="mt-1 whitespace-pre-line">"{quote.customer_decision_note}"</p>}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4" data-testid="quote-quick-view-actions">
          <Button size="sm" variant="outline" onClick={() => actions.downloadPDF(quote.id, quote.quote_number)} data-testid="quote-qv-download"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => actions.emailQuote(quote.id)} data-testid="quote-qv-email"><Mail className="w-4 h-4 mr-1" /> Email</Button>
          {quote.status === "draft" && (
            <>
              <Button size="sm" variant="outline" onClick={run(() => actions.onEdit(quote))} data-testid="quote-qv-edit"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={run(() => actions.submitForApproval(quote.id))} data-testid="quote-qv-submit"><Send className="w-4 h-4 mr-1" /> Submit for approval</Button>
            </>
          )}
          {quote.status === "pending_approval" && currentUser?.role === "admin" && (
            <>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={run(() => actions.approve(quote.id))} data-testid="quote-qv-approve"><Check className="w-4 h-4 mr-1" /> Approve</Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={run(() => actions.reject(quote.id))} data-testid="quote-qv-reject"><X className="w-4 h-4 mr-1" /> Reject</Button>
            </>
          )}
          {quote.status === "approved" && !quote.converted_invoice_number && (
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={run(() => actions.convertToInvoice(quote.id))} data-testid="quote-qv-convert"><DollarSign className="w-4 h-4 mr-1" /> Convert to invoice</Button>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs uppercase text-slate-500">Quote date</p><p className="font-medium text-slate-800">{String(quote.created_at || "").slice(0, 10)}</p></div>
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs uppercase text-slate-500">Valid until</p><p className={`font-medium ${isExpired(quote) ? "text-red-600" : "text-slate-800"}`}>{quote.valid_until}</p></div>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-700 mb-2">Line items</p>
          <div className="rounded-lg border divide-y" data-testid="quote-quick-view-items">
            {(quote.items || []).map((it, i) => (
              <div key={i} className="p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <p className="text-slate-800 whitespace-pre-line flex-1">{it.description}</p>
                  <p className="font-semibold text-slate-800 whitespace-nowrap">{money(it.amount)}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1">{it.quantity} × {money(it.rate)}</p>
              </div>
            ))}
            {(quote.items || []).length === 0 && <p className="p-3 text-sm text-slate-400">No line items</p>}
          </div>
        </div>

        <div className="mt-4 rounded-lg border p-3" data-testid="quote-quick-view-totals">
          <Row label="Subtotal" value={money(quote.subtotal)} />
          <Row label="Tax" value={money(quote.tax)} />
          <Row label="Total" value={money(quote.total)} cls="text-base text-indigo-700" />
        </div>

        {quote.notes && <div className="mt-4"><p className="text-sm font-semibold text-slate-700 mb-1">Notes</p><p className="text-sm text-slate-600 whitespace-pre-line rounded-lg bg-slate-50 p-3" data-testid="quote-quick-view-notes">{quote.notes}</p></div>}
      </SheetContent>
    </Sheet>
  );
};
